import { fillTextBox, measureText } from '@remotion/layout-utils';

import { SCENE5B_FONTS, SCENE5B_LAYOUT } from '../constants';

const measureProps = {
  fontFamily: SCENE5B_FONTS.title.fontFamily as string,
  fontWeight: SCENE5B_FONTS.title.fontWeight,
  letterSpacing: SCENE5B_FONTS.title.letterSpacing,
};

const fillProps = {
  fontFamily: SCENE5B_FONTS.title.fontFamily as string,
  fontWeight: SCENE5B_FONTS.title.fontWeight,
  additionalStyles: { letterSpacing: SCENE5B_FONTS.title.letterSpacing },
};

const availableWidth =
  SCENE5B_LAYOUT.title.safeAreaWidth - SCENE5B_LAYOUT.title.paddingLeft;

// Check if text fits within maxLines at a given fontSize
const isWithinMaxLines = (text: string, fontSize: number): boolean => {
  const box = fillTextBox({
    maxBoxWidth: availableWidth,
    maxLines: SCENE5B_LAYOUT.title.maxLines,
  });
  return text
    .split(' ')
    .every(
      (word) => !box.add({ text: word, fontSize, ...fillProps }).exceedsBox,
    );
};

// Find the largest font size fitting most titles within maxLines
export const calculateTitleFontSize = (titles: string[]): number => {
  const {
    baseFontSize,
    minFontSize,
    maxLines,
    safeAreaHeight,
    lineHeightRatio,
  } = SCENE5B_LAYOUT.title;
  const heightMax = Math.floor(safeAreaHeight / (maxLines * lineHeightRatio));
  const maxFont = Math.min(baseFontSize, heightMax);
  const threshold = Math.ceil(titles.length / 2);
  const allWords = titles.flatMap((t) => t.split(' '));

  for (let fs = maxFont; fs >= minFontSize; fs -= 2) {
    // Skip if any word overflows at this font size
    const wordsFit = allWords.every(
      (w) =>
        measureText({ text: w, fontSize: fs, ...measureProps }).width <=
        availableWidth,
    );
    if (!wordsFit) continue;

    // Accept when ≥ half the titles fit without truncation
    if (titles.filter((t) => isWithinMaxLines(t, fs)).length >= threshold)
      return fs;
  }

  return minFontSize;
};

// Truncate text to maxLines, appending "..." if needed
export const truncateTitle = (text: string, fontSize: number): string => {
  if (isWithinMaxLines(text, fontSize)) return text;

  const words = text.split(' ');

  while (words.length > 1) {
    words.pop();
    const candidate = words.join(' ') + '...';
    if (isWithinMaxLines(candidate, fontSize)) return candidate;
  }

  return words[0] + '...';
};
